import { useState } from 'react';
import { type QueryClient, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import {
  getGetMyEnrollmentQueryKey,
  getListEnrollmentsQueryKey,
  useCreateEnrollment,
  useListEnrollments,
  useListPlans,
  useListStudents,
  useListTeachers,
  useTerminateEnrollment,
} from '../../api/generated/enrollment/enrollment';
import type { Enrollment } from '../../api/generated/model';
import { isProblemError } from '../../api/client';
import { Alert } from '../../design/primitives/Alert';
import { Button } from '../../design/primitives/Button';
import { Dialog } from '../../design/primitives/Dialog';
import { Field } from '../../design/primitives/Field';
import { AdminOnly, usePager } from './AdminOnly';
// Erro e vazio nunca juntos: a regra mora num lugar so, e nao em cada tela.
import { ListOutcome } from './ListOutcome';
import { SelectField } from './SelectField';
import styles from './enrollment.module.css';

const MAX_REASON_LENGTH = 500;

/**
 * Quanto cada seletor desta tela pede de uma vez.
 *
 * Sem `limit`, a listagem devolve a primeira pagina — e a partir do 51o aluno
 * ativo a pessoa simplesmente deixa de existir para quem opera: esta e a UNICA
 * tela de criacao de matricula da aplicacao, e o que o seletor nao mostra nao
 * pode ser matriculado. 200 e o maximo que o contrato aceita (`limit`,
 * `@maximum 200`), e nao um numero escolhido por gosto.
 *
 * Nao e a solucao final: com mais de 200 cadastros ativos o seletor continua
 * incompleto, e a saida durável e busca no servidor por texto. Ate la a tela
 * AVISA quando truncou, em vez de mentir por omissao — operador que ve o aviso
 * sabe que a pessoa existe e nao esta na lista; sem ele, conclui que o cadastro
 * sumiu.
 */
const SELECTOR_LIMIT = 200;

/**
 * Matriculas: o vinculo entre aluno, plano e professor. E a criacao dela que
 * abre sala, registro academico e caixa de mensagens; o encerramento fecha o
 * envio e preserva o historico.
 */
export function EnrollmentsPage() {
  return <AdminOnly>{() => <EnrollmentsView />}</AdminOnly>;
}

/** As duas leituras que uma matricula criada ou encerrada torna velhas. */
async function invalidateEnrollments(queryClient: QueryClient): Promise<void> {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: getListEnrollmentsQueryKey() }),
    queryClient.invalidateQueries({ queryKey: getGetMyEnrollmentQueryKey() }),
  ]);
}

function EnrollmentsView() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const pager = usePager();
  const list = useListEnrollments(pager.cursor === undefined ? {} : { cursor: pager.cursor });
  const [terminating, setTerminating] = useState<Enrollment | null>(null);

  const items = list.data?.items ?? [];
  const nextCursor = list.data?.nextCursor ?? null;

  return (
    <div className={styles.page}>
      <h1>{t('enrollment.enrollments.title')}</h1>

      <ListOutcome
        isPending={list.isPending}
        isError={list.isError}
        count={items.length}
        empty={t('enrollment.enrollments.empty')}
      />

      <table className={styles.table}>
        <caption className="visually-hidden">{t('enrollment.enrollments.title')}</caption>
        <thead>
          <tr>
            <th scope="col">{t('enrollment.student')}</th>
            <th scope="col">{t('enrollment.teacher')}</th>
            <th scope="col">{t('enrollment.startsOn')}</th>
            <th scope="col">{t('enrollment.endsOn')}</th>
            <th scope="col">{t('enrollment.status')}</th>
            <th scope="col">{t('enrollment.actions')}</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr key={item.id}>
              <td>{item.student.displayName}</td>
              <td>{item.teacher.displayName}</td>
              <td>{item.startsOn}</td>
              <td>{item.endsOn ?? '—'}</td>
              <td>{item.active ? t('enrollment.active') : t('enrollment.ended')}</td>
              <td>
                {item.active ? (
                  <Button variant="danger" onClick={() => setTerminating(item)}>
                    {t('enrollment.enrollments.terminate')}
                  </Button>
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className={styles.pager}>
        <Button disabled={!pager.canGoBack} onClick={pager.goBack}>
          {t('enrollment.previousPage')}
        </Button>
        <Button
          disabled={nextCursor === null}
          onClick={() => {
            if (nextCursor !== null) pager.goForward(nextCursor);
          }}
        >
          {t('enrollment.nextPage')}
        </Button>
      </div>

      {/*
        Matricula criada ou encerrada muda tambem a tela "Minha matricula" do
        aluno, que le outro recurso. Refazer so a listagem desta pagina deixava
        a outra afirmando vinculo ativo depois do encerramento.
      */}
      <EnrollmentForm onCreated={() => void invalidateEnrollments(queryClient)} />

      <TerminateDialog
        enrollment={terminating}
        onClose={() => setTerminating(null)}
        onTerminated={() => {
          setTerminating(null);
          void invalidateEnrollments(queryClient);
        }}
      />
    </div>
  );
}

function EnrollmentForm({ onCreated }: { readonly onCreated: () => void }) {
  const { t } = useTranslation();
  const create = useCreateEnrollment();
  const students = useListStudents({ active: true, limit: SELECTOR_LIMIT });
  const teachers = useListTeachers({ limit: SELECTOR_LIMIT });
  const plans = useListPlans();

  // `nextCursor` preenchido significa que ha cadastro alem do que o seletor
  // carregou. A tela diz isso; quem opera precisa saber que a ausencia da
  // pessoa na lista nao e ausencia no cadastro.
  const truncated =
    (students.data?.nextCursor ?? null) !== null || (teachers.data?.nextCursor ?? null) !== null;

  const [studentId, setStudentId] = useState('');
  const [teacherId, setTeacherId] = useState('');
  const [planId, setPlanId] = useState('');
  const [startsOn, setStartsOn] = useState('');

  const canSave = studentId !== '' && teacherId !== '' && planId !== '' && startsOn !== '';

  return (
    <form
      className={styles.form}
      aria-label={t('enrollment.enrollments.create')}
      onSubmit={(event) => {
        event.preventDefault();
        if (!canSave) return;
        create.mutate(
          { data: { studentId, teacherId, planId, startsOn } },
          {
            onSuccess: () => {
              setStudentId('');
              setTeacherId('');
              setPlanId('');
              setStartsOn('');
              onCreated();
            },
          },
        );
      }}
    >
      <h2>{t('enrollment.enrollments.create')}</h2>

      {truncated ? (
        <Alert tone="warning">{t('enrollment.listTruncated', { limit: SELECTOR_LIMIT })}</Alert>
      ) : null}

      <SelectField
        label={t('enrollment.student')}
        required
        value={studentId}
        placeholder={t('enrollment.choose')}
        options={(students.data?.items ?? []).map((student) => ({
          value: student.personId,
          label: student.displayName,
        }))}
        onChange={setStudentId}
      />
      <SelectField
        label={t('enrollment.teacher')}
        required
        value={teacherId}
        placeholder={t('enrollment.choose')}
        options={(teachers.data?.items ?? []).map((teacher) => ({
          value: teacher.personId,
          label: teacher.displayName,
        }))}
        onChange={setTeacherId}
      />
      <SelectField
        label={t('enrollment.plan')}
        required
        value={planId}
        placeholder={t('enrollment.choose')}
        options={(plans.data ?? []).map((plan) => ({ value: plan.id, label: plan.label }))}
        onChange={setPlanId}
      />
      <Field
        label={t('enrollment.startsOn')}
        type="date"
        required
        value={startsOn}
        onChange={(event) => setStartsOn(event.target.value)}
      />

      {create.isError ? (
        <Alert tone="error">
          {isProblemError(create.error) && create.error.status === 409
            ? t('enrollment.enrollments.conflict')
            : t('error.unexpected')}
        </Alert>
      ) : null}
      {create.isSuccess ? (
        <Alert tone="success">{t('enrollment.enrollments.created')}</Alert>
      ) : null}

      <div>
        <Button variant="primary" type="submit" loading={create.isPending} disabled={!canSave}>
          {t('enrollment.save')}
        </Button>
      </div>
    </form>
  );
}

/**
 * Encerramento pede data e justificativa porque o contrato pede as duas, e a
 * justificativa entra na trilha de auditoria. Por isso nao e o `ConfirmDialog`
 * de design: nao ha o que confirmar antes de haver o que escrever.
 */
function TerminateDialog({
  enrollment,
  onClose,
  onTerminated,
}: {
  readonly enrollment: Enrollment | null;
  readonly onClose: () => void;
  readonly onTerminated: () => void;
}) {
  const { t } = useTranslation();
  const terminate = useTerminateEnrollment();
  const [endsOn, setEndsOn] = useState('');
  const [reason, setReason] = useState('');

  const canTerminate =
    endsOn !== '' && reason.trim().length > 0 && reason.trim().length <= MAX_REASON_LENGTH;

  const close = (): void => {
    setEndsOn('');
    setReason('');
    onClose();
  };

  if (enrollment === null) return null;

  return (
    <Dialog
      open
      title={t('enrollment.enrollments.terminateTitle')}
      onClose={close}
      footer={
        <>
          <Button variant="secondary" onClick={close} disabled={terminate.isPending}>
            {t('action.cancel')}
          </Button>
          <Button
            variant="danger"
            loading={terminate.isPending}
            disabled={!canTerminate}
            onClick={() =>
              terminate.mutate(
                { enrollmentId: enrollment.id, data: { endsOn, reason: reason.trim() } },
                {
                  onSuccess: () => {
                    setEndsOn('');
                    setReason('');
                    onTerminated();
                  },
                },
              )
            }
          >
            {t('enrollment.enrollments.terminate')}
          </Button>
        </>
      }
    >
      <p>{t('enrollment.enrollments.terminateBody', { name: enrollment.student.displayName })}</p>
      <Field
        label={t('enrollment.endsOn')}
        type="date"
        required
        value={endsOn}
        onChange={(event) => setEndsOn(event.target.value)}
      />
      <Field
        label={t('enrollment.enrollments.reason')}
        hint={t('enrollment.enrollments.reasonHint')}
        required
        maxLength={MAX_REASON_LENGTH}
        value={reason}
        onChange={(event) => setReason(event.target.value)}
      />
      {terminate.isError ? <Alert tone="error">{t('error.unexpected')}</Alert> : null}
    </Dialog>
  );
}
